package fr.frux.feature.notifications

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Switch
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import fr.frux.app.resources.Res
import fr.frux.app.resources.notifprefs_categories_heading
import fr.frux.app.resources.notifprefs_email_label
import fr.frux.app.resources.notifprefs_harvests_description
import fr.frux.app.resources.notifprefs_harvests_label
import fr.frux.app.resources.notifprefs_inapp_description
import fr.frux.app.resources.notifprefs_inapp_label
import fr.frux.app.resources.notifprefs_maturity_description
import fr.frux.app.resources.notifprefs_maturity_label
import fr.frux.app.resources.notifprefs_maturity_locked
import fr.frux.app.resources.notifprefs_maturity_locked_action
import fr.frux.app.resources.notifprefs_push_label
import fr.frux.app.resources.notifprefs_push_not_available
import fr.frux.app.resources.notifprefs_push_permission_denied
import fr.frux.app.resources.notifprefs_push_registration_failed
import fr.frux.app.resources.notifprefs_push_unsupported
import fr.frux.app.resources.notifprefs_title
import fr.frux.app.resources.notifprefs_trees_description
import fr.frux.app.resources.notifprefs_trees_label
import fr.frux.core.designsystem.components.ErrorState
import fr.frux.core.designsystem.components.FruxCard
import fr.frux.core.designsystem.components.FruxTopBar
import fr.frux.core.designsystem.components.LoadingState
import fr.frux.core.designsystem.components.SnackbarMessageEffect
import fr.frux.core.designsystem.error.userMessage
import fr.frux.core.designsystem.theme.FruxSpacing
import fr.frux.shared.domain.model.NotificationPreferences
import org.jetbrains.compose.resources.stringResource

/**
 * Préférences de notification (#225 P0, ADR 0017), atteinte depuis [fr.frux.feature.settings.SettingsScreen].
 *
 * Deux usages distincts de `state.error` (revue T15) : un échec du chargement initial (`prefs ==
 * null`) bloque l'écran entier derrière [ErrorState] avec retry, alors qu'un échec d'action une
 * fois les préférences affichées (toggle push refusé, sauvegarde en échec) passe par la snackbar
 * et se referme tout seul via [NotificationPreferencesViewModel.onErrorShown] — jamais dans un
 * `remember` d'écran, qui serait détruit à chaque recomposition de l'entryProvider Nav3.
 */
@Composable
fun NotificationPreferencesScreen(
    onBack: () -> Unit,
    onOpenPremium: () -> Unit,
    viewModel: NotificationPreferencesViewModel = rememberNotificationPreferencesViewModel(),
) {
    val state by viewModel.state.collectAsState()
    val prefs = state.preferences
    val snackbarHostState = remember { SnackbarHostState() }

    SnackbarMessageEffect(
        trigger = state.error,
        message = if (prefs != null) state.error?.message() else null,
        snackbarHostState = snackbarHostState,
    ) { viewModel.onErrorShown() }

    Scaffold(
        topBar = { FruxTopBar(title = stringResource(Res.string.notifprefs_title), onBack = onBack) },
        snackbarHost = { SnackbarHost(snackbarHostState) },
        containerColor = MaterialTheme.colorScheme.background,
    ) { paddingValues ->
        when {
            state.isLoading && prefs == null -> {
                LoadingState(modifier = Modifier.padding(paddingValues))
            }

            state.error != null && prefs == null -> {
                ErrorState(
                    message = state.error!!.message(),
                    onRetry = viewModel::load,
                    modifier = Modifier.padding(paddingValues),
                )
            }

            prefs != null -> {
                Column(
                    modifier =
                        Modifier
                            .fillMaxWidth()
                            .padding(paddingValues)
                            .padding(FruxSpacing.md),
                    verticalArrangement = Arrangement.spacedBy(FruxSpacing.sm),
                ) {
                    PreferenceRow(
                        label = stringResource(Res.string.notifprefs_inapp_label),
                        description = stringResource(Res.string.notifprefs_inapp_description),
                        checked = true,
                        onCheckedChange = null,
                    )
                    PreferenceRow(
                        label = stringResource(Res.string.notifprefs_email_label),
                        checked = prefs.emailEnabled,
                        onCheckedChange = viewModel::setEmailEnabled,
                    )
                    PreferenceRow(
                        label = stringResource(Res.string.notifprefs_push_label),
                        description =
                            if (state.pushSupported) null else stringResource(Res.string.notifprefs_push_unsupported),
                        checked = prefs.pushEnabled,
                        onCheckedChange =
                            if (state.pushSupported && !state.isRegisteringPush) {
                                viewModel::setPushEnabled
                            } else {
                                null
                            },
                    )

                    Spacer(modifier = Modifier.height(FruxSpacing.md))
                    Text(
                        text = stringResource(Res.string.notifprefs_categories_heading),
                        style = MaterialTheme.typography.titleMedium,
                        color = MaterialTheme.colorScheme.onSurface,
                    )
                    Spacer(modifier = Modifier.height(FruxSpacing.xs))

                    PreferenceRow(
                        label = stringResource(Res.string.notifprefs_harvests_label),
                        description = stringResource(Res.string.notifprefs_harvests_description),
                        checked = prefs.harvestEvents,
                        onCheckedChange = viewModel::setHarvestEvents,
                    )
                    PreferenceRow(
                        label = stringResource(Res.string.notifprefs_trees_label),
                        description = stringResource(Res.string.notifprefs_trees_description),
                        checked = prefs.treeEvents,
                        onCheckedChange = viewModel::setTreeEvents,
                    )
                    MaturityPreferenceRow(
                        prefs = prefs,
                        onCheckedChange = viewModel::setMaturityAlerts,
                        onOpenPremium = onOpenPremium,
                    )
                }
            }
        }
    }
}

/** Option verrouillée (#455) : visible, interrupteur inactif, lien vers l'écran Frux+. */
@Composable
private fun MaturityPreferenceRow(
    prefs: NotificationPreferences,
    onCheckedChange: (Boolean) -> Unit,
    onOpenPremium: () -> Unit,
) {
    val locked = prefs.maturityAlertsLocked
    PreferenceRow(
        label = stringResource(Res.string.notifprefs_maturity_label),
        description =
            stringResource(
                if (locked) Res.string.notifprefs_maturity_locked else Res.string.notifprefs_maturity_description,
            ),
        checked = prefs.maturityAlerts,
        onCheckedChange = if (locked) null else onCheckedChange,
        action =
            if (locked) {
                {
                    TextButton(onClick = onOpenPremium) {
                        Text(stringResource(Res.string.notifprefs_maturity_locked_action))
                    }
                }
            } else {
                null
            },
    )
}

@Composable
private fun PreferenceRow(
    label: String,
    description: String? = null,
    checked: Boolean,
    onCheckedChange: ((Boolean) -> Unit)?,
    action: (@Composable () -> Unit)? = null,
) {
    FruxCard(modifier = Modifier.fillMaxWidth()) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween,
        ) {
            Column(modifier = Modifier.weight(1f)) {
                Text(text = label, style = MaterialTheme.typography.bodyLarge)
                if (description != null) {
                    Text(
                        text = description,
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                }
                action?.invoke()
            }
            Switch(checked = checked, onCheckedChange = onCheckedChange, enabled = onCheckedChange != null)
        }
    }
}

/** Libellé du refus à annoncer sur l'écran de préférences (#176). */
@Composable
private fun NotificationPreferencesError.message(): String =
    when (this) {
        NotificationPreferencesError.PushPermissionDenied -> {
            stringResource(Res.string.notifprefs_push_permission_denied)
        }

        NotificationPreferencesError.PushUnsupported -> {
            stringResource(Res.string.notifprefs_push_not_available)
        }

        NotificationPreferencesError.PushRegistrationFailed -> {
            stringResource(Res.string.notifprefs_push_registration_failed)
        }

        is NotificationPreferencesError.Failure -> {
            error.userMessage()
        }
    }
